import FunnelLogo from "@components/quiz/FunnelLogo"
import { ArrowLeftIcon, CheckIcon } from "@heroicons/react/24/outline"
import { StarIcon } from "@heroicons/react/24/solid"
import { QUIZ_SECTIONS, QuizSection } from "@pages/onboarding/quizSteps"
import { Fragment } from "react"
import { useTranslation } from "react-i18next"

interface Props {
  onBack?: () => void
  section: QuizSection
  sectionProgress: number
}

function StepCircle({
  index,
  label,
  state,
}: {
  index: number
  label?: string
  state: "done" | "active" | "upcoming"
}) {
  return (
    <div className="relative flex shrink-0 flex-col items-center">
      <span
        className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold transition-colors duration-300 ${
          state === "upcoming"
            ? "bg-vm-line text-vm-muted"
            : "bg-vm-ink text-vm-cream"
        } ${state === "active" ? "ring-4 ring-vm-ink/10" : ""}`}
      >
        {state === "done" ? (
          <CheckIcon className="h-3.5 w-3.5 animate-check-pop" />
        ) : (
          index + 1
        )}
      </span>

      {label && (
        <span className="absolute top-7 animate-fade-in whitespace-nowrap text-[11px] font-semibold text-vm-ink">
          {label}
        </span>
      )}
    </div>
  )
}

export default function QuizHeader({
  onBack,
  section,
  sectionProgress,
}: Props) {
  const { t } = useTranslation()

  const activeSectionIndex = QUIZ_SECTIONS.indexOf(section)
  const isFinished =
    activeSectionIndex === QUIZ_SECTIONS.length - 1 && sectionProgress >= 1

  return (
    <header className="sticky top-0 z-20 bg-vm-cream/90 backdrop-blur">
      <div className="vm-container pb-7 pt-3">
        <div className="relative flex h-10 items-center justify-center">
          {onBack && (
            <button
              aria-label={t("shared.back")}
              className="absolute left-0 flex h-10 w-10 items-center justify-center rounded-full text-vm-ink transition-colors hover:bg-vm-line"
              onClick={onBack}
              type="button"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
          )}

          <FunnelLogo />
        </div>

        <div className="mt-5 flex items-center px-2">
          {QUIZ_SECTIONS.map((quizSection, index) => {
            const state =
              index < activeSectionIndex
                ? "done"
                : index === activeSectionIndex
                  ? "active"
                  : "upcoming"
            const fill =
              state === "done" ? 1 : state === "active" ? sectionProgress : 0

            return (
              <Fragment key={quizSection}>
                <StepCircle
                  index={index}
                  label={
                    state === "active"
                      ? t(`pages.onboarding.sections.${quizSection}`)
                      : undefined
                  }
                  state={state}
                />
                <div className="mx-1 h-1 flex-1 overflow-hidden rounded-full bg-vm-line">
                  <div
                    className="h-full rounded-full bg-vm-ink transition-[width] duration-500 ease-out"
                    style={{ width: `${fill * 100}%` }}
                  />
                </div>
              </Fragment>
            )
          })}

          <span
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors duration-300 ${
              isFinished
                ? "bg-vm-ink text-vm-cream"
                : "bg-vm-line text-vm-muted"
            }`}
          >
            <StarIcon className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </header>
  )
}
